import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';

const C = { green: '#3cb74e', black: '#111111', white: '#ffffff', red: '#ef4444', gray: '#64748b', border: '#e2e8f0' };

export default function LoginPage() {
  const [tab, setTab]       = useState('client');
  const [form, setForm]     = useState({ phone: '', password: '' });
  const [error, setError]   = useState('');
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const { login }           = useAuth();
  const navigate            = useNavigate();

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async () => {
    if (!form.phone || !form.password) return setError('Phone number and password are required');
    if (!/^\d{10}$/.test(form.phone))  return setError('Phone must be exactly 10 digits');
    setError(''); setLoading(true);
    try {
      const endpoint = tab === 'admin' ? '/auth/admin/login' : '/auth/client/login';
      const res = await api.post(endpoint, form);
      login(res.data.token, res.data.user);
      navigate(tab === 'admin' ? '/admin/dashboard' : '/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed. Please try again.');
    } finally { setLoading(false); }
  };

  return (
    <div style={s.page}>
      <div style={s.card}>
        {/* Brand */}
        <div style={s.brand}>
          <span style={s.brandIcon}>✉️</span>
          <div>
            <h1 style={s.brandName}>EmailVerifier</h1>
            <p style={s.brandSub}>msdiscoveries.com</p>
          </div>
        </div>

        <p style={s.welcomeText}>Welcome back! Sign in to your account</p>

        {/* Role tabs */}
        <div style={s.tabs}>
          {[
            { key: 'client', icon: '👤', label: 'Client Login' },
            { key: 'admin',  icon: '🔐', label: 'Admin Login'  },
          ].map(({ key, icon, label }) => (
            <button key={key}
              style={{ ...s.tab, ...(tab === key ? s.tabActive : {}) }}
              onClick={() => { setTab(key); setError(''); setForm({ phone: '', password: '' }); }}>
              {icon} {label}
            </button>
          ))}
        </div>

        {/* Error */}
        {error && (
          <div style={s.errorBox}>
            <span style={s.errorIcon}>⚠️</span>
            <span>{error}</span>
          </div>
        )}

        {/* Fields */}
        <div style={s.fieldGroup}>
          <label style={s.label}>Phone Number</label>
          <input
            style={s.input}
            name="phone" type="tel"
            placeholder="Enter 10-digit phone number"
            value={form.phone}
            onChange={handleChange}
            maxLength={10}
          />
        </div>

        <div style={s.fieldGroup}>
          <label style={s.label}>Password</label>
          <div style={s.pwdWrap}>
            <input
              style={{ ...s.input, paddingRight: 44, marginBottom: 0 }}
              name="password"
              type={showPwd ? 'text' : 'password'}
              placeholder="Enter your password"
              value={form.password}
              onChange={handleChange}
              onKeyDown={e => e.key === 'Enter' && handleSubmit()}
            />
            <button style={s.eyeBtn} onClick={() => setShowPwd(p => !p)} tabIndex={-1}>
              {showPwd ? '🙈' : '👁️'}
            </button>
          </div>
        </div>

        <button style={{ ...s.submitBtn, opacity: loading ? 0.75 : 1 }} onClick={handleSubmit} disabled={loading}>
          {loading
            ? <span>⏳ Signing in...</span>
            : <span>{tab === 'admin' ? '🔐 Sign In as Admin' : '👤 Sign In as Client'}</span>
          }
        </button>

        <p style={s.registerHint}>
          Don't have an account?{' '}
          <Link to="/register" style={s.registerLink}>Register with invite link</Link>
        </p>
      </div>
    </div>
  );
}

const s = {
  page:        { minHeight: '100vh', background: '#111111', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
  card:        { background: '#ffffff', borderRadius: 20, padding: '40px 36px', width: '100%', maxWidth: 420, boxShadow: '0 20px 60px rgba(0,0,0,0.4)' },
  brand:       { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 },
  brandIcon:   { fontSize: 40 },
  brandName:   { color: '#3cb74e', fontSize: 24, fontWeight: 800, margin: 0 },
  brandSub:    { color: '#64748b', fontSize: 11, margin: 0 },
  welcomeText: { color: '#64748b', fontSize: 13, margin: '0 0 24px' },
  tabs:        { display: 'flex', gap: 8, marginBottom: 22 },
  tab:         { flex: 1, padding: '10px 8px', borderRadius: 10, border: '1.5px solid #e2e8f0', background: '#f8fafc', color: '#64748b', cursor: 'pointer', fontWeight: 600, fontSize: 13, transition: 'all .15s' },
  tabActive:   { background: '#3cb74e', color: '#ffffff', border: '1.5px solid #3cb74e' },
  errorBox:    { display: 'flex', alignItems: 'center', gap: 8, background: '#fef2f2', border: '1.5px solid #fca5a5', color: '#991b1b', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 16 },
  errorIcon:   { flexShrink: 0 },
  fieldGroup:  { marginBottom: 14 },
  label:       { display: 'block', color: '#111111', fontSize: 12, fontWeight: 700, marginBottom: 6 },
  input:       { width: '100%', padding: '12px 14px', borderRadius: 9, border: '1.5px solid #e2e8f0', background: '#f8fafc', color: '#111111', fontSize: 14, outline: 'none', boxSizing: 'border-box', marginBottom: 0 },
  pwdWrap:     { position: 'relative' },
  eyeBtn:      { position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, padding: 4 },
  submitBtn:   { width: '100%', padding: '13px', background: '#3cb74e', color: '#ffffff', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 15, cursor: 'pointer', marginTop: 8, letterSpacing: '.2px' },
  registerHint:{ color: '#64748b', fontSize: 13, textAlign: 'center', marginTop: 18 },
  registerLink:{ color: '#3cb74e', textDecoration: 'none', fontWeight: 600 },
};
